import { authorizeRequest } from '@u/http/authorizeRequest'
import { apiOrigin, baseURL } from '@u/http/apiOrigin'
import { createHttpClient } from '@u/http/createHttpClient'
import store from '../store'
import Bugsnag from '@bugsnag/js'
import { helper } from '@u/helper'
import { clearObservedUser } from '@/observability/faro'
import { redirectToLogin } from '@/router/utils/loginRedirect'

export { apiOrigin, baseURL }

/**
 * Handles every failed API call: toasts, validation errors into the auth
 * store, redirect on 401. Rejecting keeps the caller's `.catch`; returning
 * (the 401 case) resolves the call, as the former axios interceptor did.
 */
function handleResponseError(error) {
  if (error.code === 'ERR_CANCELED') return Promise.reject(error)
  if (error.response) {
    let response_data = error.response.data
    // An external partner (Elys, …) failed: whatever the status, show its
    // message branded with the partner's logo instead of a generic toast.
    if (response_data?.partner) {
      helper.toastPartnerError(response_data)
      return Promise.reject(error)
    }
    switch (error.response.status) {
      case 400:
        if (Object.keys(response_data.details).length) {
          if (
            store?.getters?.['auth/getAuth']?.roles?.includes('super-admin')
          ) {
            helper.toastAction('warning', response_data.message, null, [
              {
                title: 'See details',
                action: () => {
                  store.commit(
                    'electronicSignature/setErrorDetails',
                    response_data.details
                  )
                }
              }
            ])
          } else {
            helper.toastWarning(response_data.message)
          }
        } else if (response_data.message) {
          helper.toastWarning(response_data.message)
        }

        return Promise.reject(error)
      case 401:
        store.commit('auth/setErrors', response_data.errors)
        clearObservedUser()
        redirectToLogin()
        break
      case 403:
        if (!error.config?.skipForbiddenToast) {
          helper.toastWarning(
            response_data.errors?.permissions ??
              response_data.errors?.roles ??
              'You are not authorised to perform this action.'
          )
        }
        return Promise.reject(error)
      case 404:
      case 408:
        if (!error.config?.skipNotFoundToast) {
          helper.toastDanger(
            response_data?.errors?.message ?? 'something went wrong'
          )
        }
        return Promise.reject(error)
      // A conflict with the current state (e.g. the same cheque number being
      // recorded by another request). Callers showing their own message pass
      // skipConflictToast.
      case 409:
        if (!error.config?.skipConflictToast && response_data?.message) {
          helper.toastWarning(response_data.message)
        }
        return Promise.reject(error)
      case 422:
        if (!error.config?.skipValidationToast) {
          helper.toastWarning(
            response_data?.message ??
              'des erreurs sont présentes dans le formulaire'
          )
        }
        store.commit('auth/setErrors', response_data.errors)
        return Promise.reject(error)
      case 429:
        helper.toastWarning(response_data?.errors?.throttle)
        return Promise.reject(error)
      case 500:
        helper.toastDanger('Erreur serveur')
        store.commit('auth/setErrors', { 500: [] })
        return Promise.reject(error)
      default:
        Bugsnag.notify(error)
        return Promise.reject(error)
    }
  } else {
    helper.toastDanger('erreur serveur')
    return Promise.reject(error)
  }
}

const apiClient = createHttpClient({
  baseURL: apiOrigin,
  onRequest: authorizeRequest,
  onResponseError: handleResponseError
})

export default apiClient
